<template>
  <div class="basic-chart-wrapper">
    <canvas
      ref="canvasRef"
      v-show="!isEmpty(pointList) && loadAllImages"
      :class="['matrix-canvas', `matrix-${chartType}${uuidv4Num}`, chartType]"
      @click="handleCanvasClick"
      @mousemove="isShowTooltip && handleMouseMove"
      @mouseleave="hideTooltip"
    >
    </canvas>
    <!-- Overlay grid for tooltips -->
    <div v-if="chartType === 'road' && chartFights.length > 0" class="tooltip-grid">
      <div
        v-for="(point, index) in fightDataList"
        :key="index"
        class="grid-cell"
        :style="{
          left: point.x + 'px',
          top: point.y + 'px',
          width: chartProps.gap + 'px',
          height: chartProps.gap + 'px',
        }"
        @mouseover="showTooltip(index, $event)"
        @mouseleave="hideTooltip"
      >
        <div v-if="tooltipIndex === index" class="chart-tooltip">
          <div class="tooltip-content">
            <template v-if="tooltipData.isDraw">
              <!-- Draw result with previous result -->
              <div class="draw-tooltip">
                <div class="previous-result">
                  <span class="fight-number">#{{ tooltipData.previousFight }}</span>
                  <img :src="getTooltipIcon(point.val.previousResult)" class="result-icon" alt="previous result" />
                  <span class="odds">{{ tooltipData.previousOdds }}x</span>
                </div>
                <div class="current-result">
                  <span class="fight-number">#{{ point.val.fight }}</span>
                  <img :src="getTooltipIcon(2)" class="result-icon" alt="draw result" />
                  <span class="odds">{{ tooltipData.currentOdds }}x</span>
                </div>
              </div>
            </template>
            <template v-else>
              <!-- Normal result -->
              <span class="fight-number">#{{ point.val.fight }}</span>
              <img v-if="point.val.fight" :src="getTooltipIcon(point.val.result)" class="result-icon" alt="result" />
              <span class="odds">{{ getOddFromFight(point.val.fight) }}x</span>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  drawGridLines,
  drawText,
  getSummaryData,
  getRoadChartData,
  convertDataToRoad2DArray,
  convertFightDataToRoad2DArray,
} from '@/utils/charts/mini-game-chart';
import { getRatio, hexToRgb, mathRound } from '@/utils/helpers';
import { BREAKPOINTS, DEFAULT_VAL } from '@/utils/constant';
import { isEmpty, debounce, cloneDeep } from 'lodash';
import { ChartOptions, ChartTypes, ChartDataType } from '@/utils/index.model';

export default {
  props: {
    isShowTooltip: {
      type: Boolean,
      default: false,
    },
    chartProps: {
      type: Object,
      default: () => ({
        col: 0,
        row: 0,
        gap: 1,
        imgSize: 25,
        imgOffset: 1,
        textColor: null,
        lineScale: 1,
        gridLineColor: '',
        gridBorderColor: '',
        isShowLabel: false,
        defaultFontFamily: 'Inter',
      }),
    },
    pointList: {
      type: Array,
      default: () => [],
      required: true,
    },
    imageList: {
      type: Array,
      default: () => [],
    },
    chartType: {
      type: String,
      default: ChartTypes.summary,
    },
    chartDataType: {
      type: String,
      default: ChartDataType.text,
    },
    chartFights: {
      type: Array,
      default: () => [],
    },
    fightListSummary: {
      type: Array,
      default: () => [],
    },
    fightList: {
      type: Array,
      default: () => [],
    },
  },
  data: () => {
    return {
      radiusOffset: 2,
      canvas: '',
      isEmpty,
      loadedImagesCount: 0,
      loadAllImages: false,
      loadedImages: [],
      chartDataList: [],
      selectedPoint: null,
      tooltipIndex: null,
      tooltipPosition: {
        x: 0,
        y: 0,
      },
      tooltipData: {
        fight: null,
        odds: '',
      },
      fightDataList: [],
    };
  },
  mounted() {
    this.getScaleOfCanvas = debounce(this.getScaleOfCanvas, 300);
    if (!isEmpty(this.imageList)) {
      for (const imageSrc of this.imageList) {
        const img = new Image();
        img.src = imageSrc;
        this.loadedImages.push(img);

        img.onload = () => {
          this.loadedImagesCount++;
          if (this.loadedImagesCount === this.imageList.length) {
            this.loadAllImages = true;
          }
        };
      }
    } else {
      this.loadAllImages = true;
    }
  },
  watch: {
    pointList: {
      handler(newVal) {
        if (isEmpty(newVal)) this.drawCanvas();
        if (this.chartDataType === ChartDataType.image && !this.loadAllImages) return;
        this.generateChartDataList();
      },
      immediate: true,
    },
    loadAllImages(newVal) {
      if (!newVal) return;
      if (isEmpty(this.chartDataList)) {
        this.generateChartDataList();
      } else {
        this.drawCanvas();
      }
    },
  },
  computed: {
    uuidv4Num() {
      return `input-${this._uid}`;
    },
    getSlashLength() {
      const val = Math.pow(this.chartProps.gap, 2) + Math.pow(this.chartProps.gap, 2);
      const valSqrt = Math.sqrt(val);
      return valSqrt;
    },
    matrixConstant() {
      const { gap, col, row } = this.chartProps;
      const widthOrigin = col * gap;
      const heightOrigin = row * gap;
      const halfGap = gap / 2;

      return {
        gap,
        widthOrigin,
        heightOrigin,
        halfGap,
      };
    },
  },
  methods: {
    getScaleOfCanvas(val) {
      DEFAULT_VAL.INIT_GAP_NUM =
        val <= BREAKPOINTS.desktop ? getRatio(this.$screen.width, BREAKPOINTS.desktop, 14.5) : 14.5;
      this.updateChart++;
    },
    drawCanvas() {
      const { gap, widthOrigin, heightOrigin, halfGap } = this.matrixConstant;

      const lineNum = {
        wDistance: gap,
        wLength: heightOrigin,
        hDistance: gap,
        hLength: widthOrigin,
      };
      const radius = mathRound(halfGap);

      let ctx;
      this.canvas = this.$refs.canvasRef;
      if (!this.canvas) return;
      ctx = this.canvas.getContext('2d');

      if (this.chartType) {
        this.canvas.addEventListener('click', () => {
          this.$emit('updatePosition', this.chartType);
        });
      }

      ctx.canvas.width = widthOrigin;
      ctx.canvas.height = heightOrigin;
      if (this.chartProps.gridBorderColor || this.chartProps.gridLineColor) {
        drawGridLines(lineNum, this.canvas, this.chartProps, ctx);
      }
      this.drawChartLabels(gap, ctx);
      this.drawAllPoints(halfGap, radius, gap, ctx);
    },
    drawAllPoints(halfGap, radius, gap, ctx) {
      const { imgOffset, imgSize } = this.chartProps;

      this.chartDataList.forEach((point, index) => {
        const { x, y, val, col, fontColor, bgBegin, bgEnd, border } = point;

        const txtCol = this.textColor ? fontColor : '#fff';
        switch (this.chartDataType) {
          case ChartDataType.image:
            if (this.loadAllImages && typeof val === 'object') {
              this.drawSVGImageFromLists(x + imgOffset, y + imgOffset, val, imgSize, ctx);
            }
            break;
          case ChartDataType.numberImage:
            this.drawCircle(x + halfGap, y + halfGap, radius, col, col, bgBegin, bgEnd, border, ctx);
            break;
          case ChartDataType.text:
            this.drawText(x + halfGap, y + halfGap, val, gap * 0.7, this.defaultFontFamily, txtCol, 'normal', ctx);
            break;
        }

        if (this.selectedPoint && this.chartType === ChartTypes.summary) {
          const fight = this.fightListSummary[index];
          if (fight) {
            const circleRadius = gap * 0.4;
            ctx.beginPath();
            ctx.arc(x + gap / 2.5, y + gap / 2.5, circleRadius, 0, 2 * Math.PI);
            switch (fight.result.toLowerCase()) {
              case 'banker':
                ctx.fillStyle = 'rgb(255, 0, 0)';
                break;
              case 'player':
                ctx.fillStyle = 'rgb(14, 134, 212)';
                break;
              case 'draw':
                ctx.fillStyle = 'rgb(24, 124, 64)';
                break;
              default:
                ctx.fillStyle = 'rgb(0, 0, 0)';
            }

            ctx.fill();

            this.drawText(
              x + gap / 2.5,
              y + gap / 2.5,
              fight.fightNumber.toString(),
              gap * 0.4,
              this.chartProps.defaultFontFamily,
              '#fff',
              'normal',
              ctx
            );
          }
        }
      });
    },
    drawCircle(xCor, yCor, radius, fillStyle, fillBorderStyle, bgBegin, bgEnd, borderColor, ctx) {
      let bgColorStyle;
      let borderColorStyle;
      if (bgBegin && bgEnd) {
        const range = 2 / 3;
        const originTopBot = {
          x0: mathRound(xCor),
          y0: mathRound(yCor - radius),
          x1: mathRound(xCor),
          y1: mathRound(yCor - radius + radius * 2 * range),
        };
        bgColorStyle = ctx.createLinearGradient(originTopBot.x0, originTopBot.y0, originTopBot.x1, originTopBot.y1);
        bgColorStyle.addColorStop(0, bgBegin);
        bgColorStyle.addColorStop(1, bgEnd);
        borderColorStyle = borderColor;
      } else {
        bgColorStyle = fillStyle;
        borderColorStyle = fillBorderStyle;
      }

      const circleRadius = radius - this.radiusOffset < 0 ? radius : radius - this.radiusOffset;

      ctx.beginPath();
      ctx.arc(xCor, yCor, circleRadius, 0, 2 * Math.PI);
      ctx.closePath();
      ctx.strokeStyle = borderColorStyle;
      ctx.fillStyle = bgColorStyle;
      ctx.fill();
      ctx.lineWidth = 1;
      ctx.stroke();
    },
    drawText(xCor, yCor, val, fontSize, fontFamily, fillStyle, fontWeight, ctx) {
      return drawText(xCor, yCor, val, fontSize, fontFamily, fillStyle, 'center', 'middle', ctx);
    },
    drawSVGImageFromLists(xCor, yCor, val, imageSize, ctx) {
      ctx.drawImage(val, xCor, yCor, imageSize, imageSize);
    },
    drawCrossSlash(xCor, yCor, gap, val, col, ctx) {
      const length = this.getSlashLength;
      const scale = 1;

      const [po1, po2, po3, po4] = [xCor + gap * scale, yCor, xCor, yCor + gap * scale];

      ctx.beginPath();

      ctx.moveTo(po1, po2);
      ctx.lineTo(po3, po4);

      ctx.fillStyle = col;
      ctx.lineWidth = 5;
      ctx.stroke();
    },
    drawChartLabels(gap, ctx) {
      const mapLabels = (anchorPointName) => {
        const mappingLabelName = {
          [ChartTypes.summary]: ChartOptions['General'],
          [ChartTypes.general]: ChartOptions['Road'],
        };

        const t = mappingLabelName[anchorPointName] || anchorPointName;
        return this.$i18n.translate(t).toUpperCase();
      };

      if (this.isShowLabel) {
        const { r, g, b } = hexToRgb.call(this, this.theme['mgIconInactive']);

        drawText(
          ctx.canvas.width / 2,
          ctx.canvas.height / 2,
          mapLabels(this.chartType),
          32,
          'Inter',
          `rgba(${r}, ${g}, ${b}, 0.6)`,
          'center',
          600,
          ctx
        );
        ctx.save();
      }
    },
    generateChartDataList() {
      const { row, col, gap, imgOffset } = this.chartProps;

      if (this.chartType === ChartTypes.summary) {
        const rawData =
          this.chartDataType === ChartDataType.image
            ? cloneDeep(this.pointList).map((p) => this.loadedImages[p])
            : cloneDeep(this.pointList);
        this.chartDataList = getSummaryData(rawData, row, col, gap, imgOffset);
      }
      if (this.chartType === ChartTypes.road) {
        this.chartDataList = getRoadChartData(
          convertDataToRoad2DArray(cloneDeep(this.pointList), row, col, this.loadedImages),
          row,
          col,
          gap,
          imgOffset
        );
        this.fightDataList = getRoadChartData(
          convertFightDataToRoad2DArray(cloneDeep(this.fightList), row, col),
          row,
          col,
          gap,
          imgOffset
        );
      }
      if (this.chartDataType !== ChartDataType.image || this.loadAllImages) {
        this.drawCanvas();
      }
    },
    handleCanvasClick(event) {
      const rect = this.$refs.canvasRef.getBoundingClientRect();
      const scaleX = this.canvas.width / rect.width;
      const scaleY = this.canvas.height / rect.height;

      const x = (event.clientX - rect.left) * scaleX;
      const y = (event.clientY - rect.top) * scaleY;

      const clickedPoint = this.getPointFromCoordinates(x, y);
      if (clickedPoint !== null) {
        this.selectedPoint = this.selectedPoint === clickedPoint ? null : clickedPoint;

        this.drawCanvas();
      }
    },
    getPointFromCoordinates(x, y) {
      const { gap, col } = this.chartProps;

      const clickedPointIndex = this.chartDataList.findIndex((point) => {
        const pointRect = {
          left: point.x,
          right: point.x + gap,
          top: point.y,
          bottom: point.y + gap,
        };

        return x >= pointRect.left && x <= pointRect.right && y >= pointRect.top && y <= pointRect.bottom;
      });

      if (clickedPointIndex >= 0) {
        return clickedPointIndex;
      }
      return null;
    },
    handleMouseMove(event) {
      const rect = this.$refs.canvasRef.getBoundingClientRect();
      const scaleX = this.canvas.width / rect.width;
      const scaleY = this.canvas.height / rect.height;

      const x = (event.clientX - rect.left) * scaleX;
      const y = (event.clientY - rect.top) * scaleY;

      const hoveredPoint = this.getPointFromCoordinates(x, y);
      if (hoveredPoint !== null) {
        const point = this.chartDataList[hoveredPoint];
        if (point && point.val) {
          this.tooltipIndex = hoveredPoint;

          // Calculate position in screen coordinates, not canvas coordinates
          const canvasRect = this.$refs.canvasRef.getBoundingClientRect();
          const pointScreenX = point.x / scaleX + canvasRect.left;
          const pointScreenY = point.y / scaleY + canvasRect.top;

          // Position at bottom right of the point
          this.tooltipPosition = {
            x: pointScreenX + this.chartProps.gap,
            y: pointScreenY + this.chartProps.gap,
          };

          const fight = this.getFightForBigRoadPosition(hoveredPoint);
          if (fight) {
            const odds = this.getOddFromFight(fight);
            this.tooltipData = {
              fight,
              odds,
            };
          }
        }
      } else {
        this.hideTooltip();
      }
    },
    hideTooltip() {
      this.tooltipIndex = null;
    },
    showTooltip(index, event) {
      if (!this.fightDataList[index]) return;

      const point = this.fightDataList[index];
      if (point && point.val !== undefined) {
        this.tooltipIndex = index;

        // Calculate position in screen coordinates
        const rect = this.$refs.canvasRef.getBoundingClientRect();
        const scaleX = this.canvas.width / rect.width;
        const scaleY = this.canvas.height / rect.height;

        const pointScreenX = point.x / scaleX + rect.left;
        const pointScreenY = point.y / scaleY + rect.top;

        // Position at bottom right of the point
        this.tooltipPosition = {
          x: pointScreenX + this.chartProps.gap,
          y: pointScreenY + this.chartProps.gap,
        };

        if (point.val) {
          if (point.val.result > 1) {
            // Find the previous fight by fight number
            const previousFightNumber = point.val.previousFight;
            const currentFightNumber = point.val.fight;

            // Use the previousFightObj directly if available
            const previousFight =
              point.val.previousFightObj || this.chartFights.find((f) => f.fightNumber === previousFightNumber);
            const currentFight = this.chartFights.find((f) => f.fightNumber === currentFightNumber);

            let previousOdds = '';
            let currentOdds = '';

            if (previousFight && previousFight.selection) {
              // Determine if previous result was banker (0) or player (1)
              const prevResultName =
                point.val.previousResult === 0 ? 'banker' : point.val.previousResult === 1 ? 'player' : 'unknown';

              const prevSelection = previousFight.selection.find((sel) => sel.name.toLowerCase() === prevResultName);
              previousOdds = prevSelection ? prevSelection.odds : '';
            }

            if (currentFight && currentFight.selection) {
              const drawSelection = currentFight.selection.find((sel) => sel.name.toLowerCase() === 'draw');
              currentOdds = drawSelection ? Number(drawSelection.odds) + 1 : '';
            }

            this.tooltipData = {
              fight: point.val.fight,
              previousFight: point.val.previousFight,
              previousOdds: previousOdds,
              currentOdds: currentOdds,
              isDraw: true,
            };
          } else {
            const fight = this.getFightForBigRoadPosition(index);
            const odds = fight ? this.getOddFromFight(fight) : '';

            this.tooltipData = {
              fight: point.val.fight,
              odds: odds || '',
              isDraw: false,
            };
          }
        }
      }
    },
    getFightForBigRoadPosition(index) {
      // Try to get the fight from the parent component first
      if (this.$parent && typeof this.$parent.getFightForRoadPosition === 'function') {
        return this.$parent.getFightForRoadPosition(index);
      }

      // Fallback to the original implementation
      const reversedIndex = this.chartDataList.length - 1 - index;
      return this.chartFights[reversedIndex];
    },

    getOddFromFight(fightNumber) {
      if (!fightNumber) {
        return '';
      }

      const fight = this.chartFights.find((f) => f.fightNumber === fightNumber);
      if (!fight) {
        return '';
      }
      const selection = fight.selection.find((sel) => sel.name.toLowerCase() === fight.result.toLowerCase());

      return selection ? selection.odds : '';
    },
    // getOddFromResult(result) {
    //   if (!result) return '';

    //   const fight = this.chartFights.find((f) => f.result && f.result.toLowerCase() === result.toLowerCase());
    //   if (!fight) return '';

    //   return this.getOddFromFight(fight);
    // },
    getTooltipIcon(result) {
      if (result === null || result === undefined) return '';

      switch (result) {
        case 0:
          return require('@/assets/images/banker-tooltip.webp');
        case 1:
          return require('@/assets/images/player-tooltip.webp');
        default:
          return require('@/assets/svg/chart-icons/draw.svg');
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.basic-chart-wrapper {
  position: relative;
  display: flex;

  canvas:not(.summary) {
    border-radius: 4px;
    border: 1px solid #2e4057;
    cursor: pointer;
  }

  .tooltip-grid {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;

    .grid-cell {
      position: absolute;
      pointer-events: auto;
      cursor: pointer;
      z-index: 2;

      &:hover {
        background-color: rgba(255, 255, 255, 0.1);
      }
    }
  }

  .chart-tooltip {
    position: absolute;
    background-color: #f0f0f0;
    color: #333;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    pointer-events: none;
    z-index: 1000;
    white-space: nowrap;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    bottom: -30px; /* Position below the point */
    left: 10px; /* Position to the right of the point */

    .tooltip-content {
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .fight-number {
      font-weight: bold;
      color: #333;
    }

    .result-icon {
      width: 20px;
      height: 20px;
      object-fit: contain;
    }

    .odds {
      color: #333;
    }
  }

  .draw-tooltip {
    display: flex;
    flex-direction: column;
    gap: 4px;

    .previous-result,
    .current-result {
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .current-result {
      border-top: 1px solid #ddd;
      padding-top: 4px;
    }
  }
}
</style>
